/** The parts of the plan panel (DESIGN §3): the bridge, a measure row with its because line, and the one-line status strip. Presentation only; the figures come from `panelModel` and `riskRows`. */
import { useId, useRef, useState, type ReactNode } from "react";
import { GxHint } from "../theme/GxHint";
import { GxPopover, useSheetMode } from "../theme/Popover";
import { GxTable, type GxColumn } from "../theme/GxTable";
import { GxNotes } from "../theme/GxNotes";
import { GxStateChip, STATE_WORD, type LimitState } from "../theme/StateChip";
import { GxTerm } from "../theme/GxTerm";
import { Icon } from "../theme/icons";
import { formatUsdPerShare } from "../format";
import { PANEL_SHORT, changeText } from "./planRows";
import { UPDATING, useProjectionPending, type KpiBridge } from "../api/riskFields";
import { printedBridge } from "./SinceYearEnd";
import { firstUseDefinition } from "../format/labels";
import { GxStatusCard } from "../theme/GxStatusCard";
import { GATED_LINE } from "./planRows";
import { HEADLINE_CAPTION } from "./planCopy";
import { PREVIEW_ROWS } from "./previewRows";
import { BRIDGE_MAX_ROWS, NO_SPLIT_LINE, type Bridge as BridgeModel, type MeasureRow as Row } from "./panelModel";
import { CLOSING_LINE, type CallRow } from "./forecast/callLine";
import { CARD_LINES_BASIS, type AnsweredCardLine } from "./answeredCards";

const signed = (x: number): string => formatUsdPerShare(x, { signed: true });
const STATE_MARK: Readonly<Record<LimitState, string>> = { clear: "\u2713", watch: "\u25B2", flag: "\u25CF" };
const toneOf = (x: number): string => (x > 0 ? "good" : x < 0 ? "crit" : "flat");

/** The share-price bridge: the title row says the price and its move, one bar per cause about a centre line, longest cause first; the largest cause's sentence is the popover on its row. */
export function Bridge({ bridge }: { readonly bridge: BridgeModel }) {
  const widest = Math.max(...bridge.rows.map((r) => Math.abs(r.value)), 0.01);
  return (
    <section className="gx-bridge" data-testid="decide.preview.bridge" aria-label="Share price, because">
      <div className="gx-bridge-t">
        <b>Share price, because</b>
        <span className="gx-num" data-testid="decide.preview.price">{bridge.level === null ? "–" : <>{"≈ "}<b>{formatUsdPerShare(bridge.level)}</b></>} <span className={`gx-plan-chg--${toneOf(bridge.change)}`}>{signed(bridge.change)}</span></span>
      </div>
      <ul className="gx-bridge-rows" data-testid="decide.preview.bridge.rows">
        {bridge.rows.map((r, i) => {
          const label = <span className="gx-bridge-n">{r.label}</span>;
          return (
            <li key={r.id} className="gx-bridge-r" data-id={r.id} data-testid={`decide.preview.bridge.${r.id}`} data-rank={i}>
              {i === 0 && bridge.cause !== null ? <GxHint text={bridge.cause} label="Why the price moved" className="gx-bridge-hint">{label}</GxHint> : label}
              <span className="gx-bridge-bar" aria-hidden="true"><span className={`gx-bridge-fill gx-bridge-fill--${r.value < 0 ? "neg" : "pos"}`} style={{ width: `${(Math.abs(r.value) / widest) * 50}%` }} /></span>
              <span className={`gx-num gx-plan-chg--${toneOf(r.value)}`}>{signed(r.value)}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** The mixed-plan parts of a row: what moves the measure each way, and the total they make. */
function MixLine({ id, mix }: { readonly id: string; readonly mix: NonNullable<Row["mix"]> }) {
  return (
    <p data-testid={`decide.preview.${id}.mix`}>
      <b>Why it is mixed:</b>{" "}
      {mix.kind === "none" ? NO_SPLIT_LINE : <>{mix.parts.map((p, i) => <span key={p.id}>{i === 0 ? "" : ", "}{p.label} <span className={`gx-num gx-plan-chg--${p.tone}`}>{p.change}</span></span>)}{" = "}<b className="gx-num">{mix.total}</b></>}
    </p>
  );
}

/** The reading's move since last year-end and every cause (the unexplained rest last) that adds to it, each at the size the row prints; the row's own change is against the no-change plan, this is against last year-end, and the sentence names which. */
function bridgeSentence(b: KpiBridge): string | null {
  const { causes } = printedBridge(b);
  if (causes.length === 0) return null;
  return `From last year-end to the start of this round, on the standing policy: ${causes.map((c) => `${c.id === "rest" ? "other" : c.label} ${changeText(b.kpi, c.move)}`).join(", ")}. The causes add to the move.`;
}

/** The change figure as a button: hover or focus opens the cause, the limit and the mixed-plan parts that used to sit under the row, so the row stays one line. */
function ChangeFigure({ row, bridge = null }: { readonly row: Row; readonly bridge?: KpiBridge | null }) {
  const [hover, setHover] = useState(false);
  const [focused, setFocused] = useState(false);
  // As a sheet (phone) the card opens on a tap and stays until closed: the sheet takes focus, so blur must not close it.
  const sheet = useSheetMode();
  const [pinned, setPinned] = useState(false);
  const anchor = useRef<HTMLSpanElement>(null);
  const id = useId();
  const cause = row.paused === true ? null : (row.full ?? row.because);
  const mix = row.mix === undefined ? null : row.mix;
  const figure = <span className={`gx-mrow-chg gx-plan-chg--${row.tone} gx-num`} data-testid={`decide.preview.${row.id}.change`}>{row.change}</span>;
  const since = bridge === null ? null : bridgeSentence(bridge);
  if (cause === null && mix === null && since === null) return <span className="gx-mrow-c" role="cell">{figure}</span>;
  const open = sheet ? pinned : hover || focused;
  return (
    <span className="gx-mrow-c" role="cell" ref={anchor} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <button type="button" className="gx-mrow-btn" data-testid={`decide.preview.${row.id}.why`} aria-describedby={open ? id : undefined} aria-label={`${row.label} change ${row.change}: why`}
        onClick={() => { if (sheet) setPinned(true); }} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}>{figure}</button>
      {open ? (
        <GxPopover anchor={anchor} id={id} label={`Why ${row.label} moved`} passive={!focused} returnFocus={false} onClose={() => { setFocused(false); setHover(false); setPinned(false); }}>
          {cause === null ? null : <p data-testid={`decide.preview.${row.id}.because`}><span className="gx-faint">Against the no-change plan. </span>{cause}{row.limit === null ? "" : ` Limit ${row.limit}.`}</p>}
          {mix === null ? null : <MixLine id={row.id} mix={mix} />}
          {since === null ? null : <p data-testid={`decide.preview.${row.id}.bridge`}>{since}</p>}
        </GxPopover>
      ) : null}
    </span>
  );
}

/** One measure on one line: its name, the forecast, the change against the standing policy (the cause is its popover) and, for a limit-bearing row, the small state chip. */
/** The rows whose figure is the saved draft's risk projection: while it is being worked out they read "Updating…", never the last version's figure or a mix. */
const PROJECTION_KPIS: readonly string[] = ["eve200", "niiSensitivity"];
export function MeasureRow({ row, bridge = null, definition = false, teamId = "" }: { readonly row: Row; /** A run is computing: a figure not yet in reads "Working out…", never a grey bar. */ readonly teamId?: string; readonly working?: boolean; /** The since-year-end causes of a bridged row (Reported or Marked CET1), for its change popover. */ readonly bridge?: KpiBridge | null; /** Print the first-use definition of Marked CET1 under its row (once per screen). */ readonly definition?: boolean }) {
  const shown = PREVIEW_ROWS.find((r) => r.id === row.id);
  // The Marked CET1 row says what it is, once on the screen: the other rows' names are links to the glossary.
  const marked = firstUseDefinition(row.kpi);
  const updating = useProjectionPending(teamId) && PROJECTION_KPIS.includes(row.kpi) && !row.locked;
  return (
    <div className="gx-mrow" role="row" data-testid={`decide.preview.${row.id}`} {...(row.locked ? { "data-gated": "true" } : {})}>
      <span className="gx-mrow-nm" role="cell" title={PANEL_SHORT[row.id] === undefined ? undefined : row.label}><GxTerm term={shown?.kpi ?? row.kpi}>{PANEL_SHORT[row.id] ?? row.label}</GxTerm></span>
      {row.locked ? <span className="gx-mrow-lock" role="cell" data-testid={`decide.preview.${row.id}.locked`}><Icon name="lock" size={13} /><span className="gx-sr">{GATED_LINE}</span></span>
        : updating ? <span className="gx-mrow-work gx-faint" role="cell" data-testid={`decide.preview.${row.id}.updating`}><span role="status" aria-live="polite">{UPDATING}</span></span>
        : row.value === null ? <span className="gx-mrow-work gx-faint" role="cell" data-testid={`decide.preview.${row.id}.working`}><span role="status">Working out…</span></span>
        : <><span className="gx-mrow-val gx-num" role="cell" data-testid={`decide.preview.${row.id}.value`}>{"≈ "}{row.value}</span><ChangeFigure row={row} bridge={bridge} /></>}
      {row.locked ? null : <span className="gx-mrow-st" role="cell">{row.state === null ? null : <span className={`gx-state gx-state--${row.state} gx-mrow-dot`} data-state={row.state} data-testid={`decide.preview.${row.id}.state`} title={STATE_WORD[row.state]}><span aria-hidden="true">{STATE_MARK[row.state]}</span><span className="gx-sr">{STATE_WORD[row.state]}</span></span>}</span>}
      {definition && marked !== null && !row.locked ? <span className="gx-mrow-note" role="cell" data-testid="decide.preview.cet1.definition">Marked CET1: {marked}</span> : null}
      {row.paused === true && row.because !== null ? <span className="gx-mrow-note" role="cell" data-testid="decide.preview.pausedbaseline">{row.because}</span> : null}
    </div>
  );
}

/** The value block as a quiet placeholder (dashes, dimmed), so the panel is its final height before any figure moves; the "no change yet" line sits over it. Hidden from assistive tech: the line says it. */
export function ReservedValue({ children }: { readonly children?: ReactNode }) {
  return (
    <div className="gx-plan-reserve-wrap"><div className="gx-plan-reserve" aria-hidden="true">
      <div className="gx-plan-vtop"><b className="gx-plan-big gx-num gx-ghost" data-ghost="≈ +$0.00" /><span className="gx-plan-vcap"><span>{HEADLINE_CAPTION}</span><span className="gx-num gx-ghost" data-ghost="likely range −$0.00 to +$0.00" /></span></div>
      <dl className="gx-plan-sum gx-num"><div><dt>Share price at year-end</dt><dd>–</dd></div><div><dt>The next two years, if the plan runs on</dt><dd>–</dd></div></dl>
    </div>{children}</div>
  );
}

const CALL_COLUMNS: readonly GxColumn[] = [{ key: "measure", label: "Measure" }, { key: "mine", label: "Your call", kind: "number", width: "4.5rem" }, { key: "theirs", label: "No-change plan", kind: "number", width: "7.5rem" }];

/** The committed call as a three-row table: Measure | Your call | No-change plan, the basis muted under each measure. */
export function CallTable({ rows, reserve = false }: { readonly rows: readonly CallRow[]; readonly reserve?: boolean }) {
  return (
    <div className={reserve ? "gx-plan-call gx-plan-call--reserve" : "gx-plan-call"} {...(reserve ? { "aria-hidden": true } : { "data-testid": "decide.preview.call" })}>
      <GxTable
        caption="Your forecast against the no-change plan" columns={CALL_COLUMNS} dense
        rows={rows.map((r) => ({ key: r.kpi, ...(reserve ? {} : { testId: `decide.preview.call.${r.kpi}` }), cells: { measure: <span className="gx-callrow-nm">{r.label}<small className="gx-faint">{r.basis}</small></span>, mine: r.mine, theirs: r.theirs } }))}
      />
      <GxNotes notes={[CLOSING_LINE]} />
      {reserve || rows[0]?.moved === undefined ? null : <p className="gx-small gx-muted" data-testid="decide.gate.moved">{rows[0].moved}</p>}
    </div>
  );
}

/** The gate before the forecast: one status card (lock, title, one sentence; the forecast card above carries the only button) at the panel's normal height, over the room the call table, the value and the bridge will take, so the box is its final height at first paint. */
export function GateBlock({ rows, quiet = false }: { readonly rows: readonly CallRow[]; /** The forecast is in but the first run is not: keep the room, say nothing over it. */ readonly quiet?: boolean }) {
  return (
    <div className="gx-plan-gateblock" data-testid="decide.preview.gate">
      <div className="gx-plan-gate-room" aria-hidden="true">
        <CallTable rows={rows} reserve />
        <div className="gx-plan-value"><ReservedValue /></div>
        <ReservedBridge />
      </div>
      {quiet ? <div className="gx-plan-gate" role="status" data-testid="decide.preview.loading"><p className="gx-plan-gate-msg">Working out your forecast…</p></div> : <div className="gx-plan-gate">
        <GxStatusCard icon="lock" title="Waiting for your forecast" testId="decide.preview.gate.msg">{GATED_LINE}</GxStatusCard>
      </div>}
    </div>
  );
}

/** The status strip's room while the first forecast is still being worked out. */
export function ReservedStatus() {
  return <div className="gx-pstatus gx-pstatus--reserve" aria-hidden="true" />;
}

/** The bridge's four rows as dashes, dimmed: the most rows a real bridge ever has, so it never grows the panel. */
export function ReservedBridge() {
  return (
    <section className="gx-bridge gx-bridge--reserve" aria-hidden="true">
      <div className="gx-bridge-t"><b>Share price, because</b><span className="gx-num">–</span></div>
      <ul className="gx-bridge-rows">{Array.from({ length: BRIDGE_MAX_ROWS }, (_, i) => <li key={i} className="gx-bridge-r"><span className="gx-bridge-n">–</span><span className="gx-bridge-bar" /><span className="gx-num">–</span></li>)}</ul>
    </section>
  );
}

/** One line per answered card: its dollar effect on the year-end share price on the card page's basis (the answer less the card's usual outcome). */
export function CardLines({ lines }: { readonly lines: readonly AnsweredCardLine[] }) {
  if (lines.length === 0) return null;
  return (
    <section className="gx-cardlines" data-testid="decide.preview.cards" aria-label="Your card answers">
      <b>Your card answers</b>
      <ul>{lines.map((l) => <li key={l.eventInstanceId} data-testid="decide.preview.cards.line">{l.line}</li>)}</ul>
      <GxNotes notes={[CARD_LINES_BASIS]} />
    </section>
  );
}

const NO_LIMIT = "No limit reached";

/** The one-line status: a state chip, the worst finding in a few words, and the way to the full dashboard. Never taller than one line. */
export function StatusStrip({ state, text, onOpen, updating = false }: { readonly state: LimitState | null; readonly text: string; readonly onOpen: () => void; /** A change is computing: the strip says so (its height does not move) and keeps its last state. */ readonly updating?: boolean }) {
  return (
    <div className="gx-pstatus" role="status" data-testid={updating ? "decide.status.updating" : "decide.status"} data-state={state ?? "clear"}>
      {state === null ? null : <GxStateChip state={state} testId="decide.status.chip" />}
      <span className="gx-pstatus-t" data-testid="decide.status.header" title={text}>{text === "" ? NO_LIMIT : text}</span>
      <button type="button" className="gx-plan-link" data-testid="decide.status.open" onClick={onOpen}>Risk dashboard {"\u203a"}</button>
    </div>
  );
}

/** The no-change state: one status card over the room the value and the bridge will take, so the box keeps its height and shows no placeholder figures. */
export function FlatState({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  return (
    <div className="gx-plan-flat" data-testid="decide.preview.flat">
      <div className="gx-plan-flat-room" aria-hidden="true"><ReservedValue /><ReservedBridge /></div>
      <div className="gx-plan-gate"><GxStatusCard icon="check" title={title}><span data-testid="decide.preview.quiet">{children}</span></GxStatusCard></div>
    </div>
  );
}
